import { useCallback, useMemo } from 'react'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'

export const NewActivitiesPie = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()
  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', in: ['GROUP', 'BROADCAST'] },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [
        { field: 'count(post)', as: 'Posts' },
        { field: 'count(reply)', as: 'Replies' },
        { field: 'count(react)', as: 'Reactions' },
      ],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Pie"
          title={$t({
            id: 'Analytics.NewActivitiesPie.ChartTitle',
            defaultMessage: 'New activities',
          })}
          description={timeFrame.text}
          data={data[1]}
          timeFrame={timeFrame}
          indexBy={['Posts', 'Replies', 'Reactions']}
          labels={[
            $t({
              id: 'Generics.Posts',
              defaultMessage: 'Posts',
            }),
            $t({
              id: 'Generics.Replies',
              defaultMessage: 'Replies',
            }),
            $t({
              id: 'Generics.Reactions',
              defaultMessage: 'Reactions',
            }),
          ]}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
